// The H3 geometry every H3 surface needs: a coordinate → the cell it falls in, a
// cell → the polygon it draws as, and the cells a drawn polygon covers.
//
// The saved geography is a set of H3 cells at one resolution per surface — the
// Planning workbench stores the ids and never the geometry, and a Signal's own
// footprint is derived on read from its stored geometry — so the grid's four
// one-form-into-another turns live here: a coordinate to a cell, a cell to the
// polygon it draws as, a drawn ring to the cells it covers, a cell to its
// centre. It is pure (no Vue, no store, no map) so all of it can be tested
// without a browser, and every resolution is an argument rather than a constant:
// an authoring grid resolves its own from the zoom (`authoringResForZoom`) while
// the saved geography uses `PLANNING_H3_RESOLUTION`, and a hidden global would
// let a click land in a cell of a grid that is not the one on screen.

import {
	cellToBoundary,
	cellToLatLng,
	getResolution,
	latLngToCell,
	polygonToCells,
} from "h3-js";
import type { Feature, FeatureCollection, Polygon } from "geojson";

/** The standard planning resolution. One value for the workbench, mirrored on
 * the server as `lead.h3_resolution` so a later change cannot reinterpret old
 * cells. It is the default a caller uses when it has no zoom of its own — an
 * editing grid resolves its resolution from the zoom instead. */
export const PLANNING_H3_RESOLUTION = 8;

/**
 * The cell a coordinate falls in, at the caller's resolution.
 *
 * The resolution is REQUIRED, not defaulted: the caller owns the grid it is
 * editing, so the cell a click lands in and the cells on screen are cut at the
 * same resolution by construction rather than by two agreements with a global.
 */
export function cellAt(
	latitude: number,
	longitude: number,
	resolution: number,
): string {
	return latLngToCell(latitude, longitude, resolution);
}

/** A cell's outline as a closed ring in [lng, lat] order, as GeoJSON carries
 * positions. h3-js is asked for GeoJSON output (`true`), which is already
 * [lng, lat], counter-clockwise and closed — so the ring is returned as it
 * answers, rather than closed again here, which would leave a duplicated vertex
 * at its end. */
export function cellBoundaryRing(cell: string): number[][] {
	return cellToBoundary(cell, true);
}

/** One cell as a polygon feature, carrying its own id in `properties.cell`
 * beside whatever the caller adds — the id is what a hit-test reads back, so a
 * feature cannot be painted from one cell and picked as another. */
export function cellFeature(
	cell: string,
	properties: Record<string, unknown>,
): Feature<Polygon> {
	return {
		type: "Feature",
		properties: { cell, ...properties },
		geometry: { type: "Polygon", coordinates: [cellBoundaryRing(cell)] },
	};
}

/** The cells as one feature collection, each with the properties the caller
 * derives from its id (a fill colour, a draft flag). */
export function cellsFeatureCollection(
	cells: string[],
	properties: (cell: string) => Record<string, unknown>,
): FeatureCollection<Polygon> {
	return {
		type: "FeatureCollection",
		features: cells.map((cell) => cellFeature(cell, properties(cell))),
	};
}

/** The cell ids covered by a drawn polygon, given in [lng, lat] order as
 * GeoJSON carries them, at the caller's resolution. h3-js's third argument says
 * the coordinates are already GeoJSON; the fill (rather than centroid) test is
 * the one an operator means by "the cells inside this shape". */
export function cellsInPolygon(ring: number[][], resolution: number): string[] {
	return polygonToCells([ring], resolution, true);
}

/**
 * A cell's centre as [lng, lat] — GeoJSON's order, which is NOT the order
 * h3-js answers in: `cellToLatLng` hands back [lat, lng], and a caller that
 * pasted that straight into a position would place every cell mirrored across
 * 45° parallel. Swapped here once, so no caller has to remember which library
 * it is holding.
 */
export function cellCentroid(cell: string): [number, number] {
	const [lat, lng] = cellToLatLng(cell);
	return [lng, lat];
}

/**
 * The resolution a cell index was cut at, which its own bit pattern states.
 *
 * A saved footprint's `h3_resolution` is a column the row may not carry (the
 * early Leads predate it), while every cell still knows its own resolution: a
 * draft opened on such a Lead is cut at the cells' own, rather than falling back
 * to the default and letting two resolutions into one Lead.
 */
export function cellResolution(cell: string): number {
	return getResolution(cell);
}
